import { Fragment } from 'react';
import { Paragraph, Heading, Text } from '@hungryroot/ramen';
import { privacySections } from '../Privacy/data';
import { getSectionId, type SectionListData } from '../SectionList/utils';
import styles from '../SectionList/SectionList.module.css';

// Ids and anchor links generated by getSectionId function in '../../components/SectionList/utils
export const statePrivacySections: SectionListData = {
  categories: {
    title: 'Categories of personal information that we collect',
    body: (
      <Fragment key="categories">
        <Paragraph className={styles['margin-bottom']}>
          In the past twelve (12) months, we may have collected the following
          categories of personal information from you:
        </Paragraph>
        <div className={styles.section}>
          <Heading variant="H6" className={styles.inline}>
            Personal Identifiers:
          </Heading>
          <Text className={styles['inline-text']}>
            Such as First and last name, alias, shipping and mailing address,
            unique personal identifier, online identifier, Internet Protocol
            (IP) address, email address, account name, or other similar
            identifiers.
          </Text>
        </div>
        <div className={styles.section}>
          <Heading variant="H6" className={styles.inline}>
            Personal information under CA Code §1798.80(e):
          </Heading>
          <Text className={styles['inline-text']}>
            Such as First and last name, postal address, telephone number,
            payment card number and information related to your account,
            including card brand and expiration date. Personal information does
            not include publicly available information that is lawfully made
            available to the general public from federal, state, or local
            government records.
          </Text>
        </div>
        <div className={styles.section}>
          <Heading variant="H6" className={styles.inline}>
            Commercial Information:
          </Heading>
          <Text className={styles['inline-text']}>
            Such as Products or services purchased from us, obtained, or
            considered, or other purchasing or consuming histories or
            tendencies.
          </Text>
        </div>
        <div className={styles.section}>
          <Heading variant="H6" className={styles.inline}>
            Financial Information (Information Described In CA Code §1798.80):
          </Heading>
          <Text className={styles['inline-text']}>
            Such as Payment card number and information related to your account,
            including card brand and expiration date.
          </Text>
        </div>
        <div className={styles.section}>
          <Heading variant="H6" className={styles.inline}>
            Internet or Network Activity:
          </Heading>
          <Text className={styles['inline-text']}>
            Such as IP address, device ID, and device settings (language
            preference, time zone), login information, browser type and version,
            browser plug-in types and versions, operating system and platform,
            information about your visit on our website or App, browsing
            history, search history, information collected via cookie or pixel
            about a consumer's interaction with our Site, App, or an
            advertisement.
          </Text>
        </div>
        <div className={styles.section}>
          <Heading variant="H6" className={styles.inline}>
            Geolocation Data:
          </Heading>
          <Text className={styles['inline-text']}>
            Such as City, state and ZIP code associated with your IP address.
          </Text>
        </div>
        <div className={styles.section}>
          <Heading variant="H6" className={styles.inline}>
            Inferences drawn from any of the categories of personal information
            listed above:
          </Heading>
          <Text className={styles['inline-text']}>
            Such as Profile reflecting a consumer’s dietary preferences and
            propensities.
          </Text>
        </div>
        <div className={styles.section}>
          <Heading variant="H6" className={styles.inline}>
            Sensitive personal information
          </Heading>
          <Text className={styles['inline-text']}>
            Such as Account log-in and password. We may also be deemed to
            collect sensitive personal information when you choose to submit
            specific dietary preferences, dietary restriction requests, and
            health goals and results from using Hungryroot (via, for example,
            surveys). This information is used to help personalize your
            subscription and/or experience with our services. These requests are
            optional and submitted directly by you through our online quiz so
            that we may suggest groceries and recipes that we think you’ll love.
          </Text>
        </div>
        <Paragraph className={styles['margin-bottom']}>
          The business and commercial purposes for which we have processed the
          categories of personal information set forth above may include (other
          than as it relates to sensitive personal information, as set forth
          below):
        </Paragraph>
        <ul className={styles['list-indented-bullet']}>
          <li>
            <Text>
              Deliver our services and carry out obligations relating to your
              periodic renewing subscription with us. For example, when you sign
              up to receive groceries through our Services, you enter into a
              subscription with us to make these grocery deliveries on your
              behalf. We must process your personal information to fulfil your
              orders.
            </Text>
          </li>
          <li>
            <Text>
              Provide, maintain, personalize, optimize, and improve the
              Services, including research and analytics regarding use of the
              Services, to provide a more personalized experience, or to
              remember you when you leave and return to the Services.
            </Text>
          </li>
          <li>
            <Text>
              Deliver our newsletter to you, whether or not you are a customer.
            </Text>
          </li>
          <li>
            <Text>
              Respond to your inquiries or requests for customer support.
            </Text>
          </li>
          <li>
            <Text>
              Effectuate billing and payment for your purchase of the Services.
            </Text>
          </li>
          <li>
            <Text>Conduct identification and authentication checks.</Text>
          </li>
          <li>
            <Text>Contact prospective customers.</Text>
          </li>
          <li>
            <Text>Conduct general research and aggregate reporting.</Text>
          </li>
          <li>
            <Text>
              Collect, process, and retain your personal information where
              required by law (for example, to fulfill our regulatory
              obligations).
            </Text>
          </li>
          <li>
            <Text>
              Provide you with more relevant content in marketing, promotional,
              or other communications to which you may be subscribed.
            </Text>
          </li>
          <li>
            <Text>
              Detect, investigate, and prevent activities that may violate our
              policies or be fraudulent or illegal.
            </Text>
          </li>
          <li>
            <Text>
              Provide you with relevant information about our products and
              services, including to help you learn about other services that we
              offer.
            </Text>
          </li>
        </ul>
        <Paragraph className={styles['margin-bottom']}>
          We use your “sensitive personal information” for the limited purposes
          specified under the CCPA in a manner that does not require a
          corresponding opt-out right, such as for the provision of our Services
          in a manner reasonably expected by an average Hungryroot customer that
          requests or uses our Services. Data obtained through the short code
          program will not be shared with any third-parties for their marketing
          reasons/purposes.
        </Paragraph>
        <Paragraph>
          For information regarding the categories of third parties with whom we
          may disclose the categories of personal information set forth in this
          section, and for which business or commercial purposes we may disclose
          such information, please see{' '}
          <a
            href={`/privacy#${getSectionId(privacySections, 'thirdParties')}`}
            className={styles.link}
            target="_blank"
          >
            here
          </a>
          .
        </Paragraph>
      </Fragment>
    ),
  },
  sources: {
    title: 'Sources of personal information',
    body: (
      <>
        <ol className={styles['list-inline-alpha']}>
          <li>
            <Text>Your use of our Services;</Text>
          </li>
          <li>
            <Text>Our Site or App;</Text>
          </li>
          <li>
            <Text>Marketing, affiliate, and analytics partners;</Text>
          </li>
          <li>
            <Text>Social media platforms; and</Text>
          </li>
          <li>
            <Text>Your responses to our surveys.</Text>
          </li>
        </ol>
        <Paragraph>
          We may also combine your personal information from the Services with
          the categories of sources listed above.
        </Paragraph>
      </>
    ),
  },
  retention: {
    title: 'Data Retention',
    body: (
      <Paragraph>
        We will retain personal information for as long as necessary to deliver
        the requested Services to you now or in future and for the purposes
        specified herein, unless a longer period is required or permitted by
        law.
      </Paragraph>
    ),
  },
  rights: {
    title: 'Your rights under State Privacy Laws',
    body: (
      <ol className={styles['list-inline-alpha']}>
        <li className={styles['item-alpha-bold']}>
          <Heading variant="H6" className={styles.inline}>
            Access:
          </Heading>
          <Text className={styles['inline-text']}>
            You may request access to the personal information that we collected
            and maintained about you (along with information regarding its use
            and disclosure), upon appropriate verification. Where we fulfil such
            requests, the information will be provided in a portable format.
          </Text>
        </li>
        <li className={styles['item-alpha-bold']}>
          <Heading variant="H6" className={styles.inline}>
            Deletion:
          </Heading>
          <Text className={styles['inline-text']}>
            You have the right to request that we delete personal information
            collected and maintained about you, subject to certain exceptions.
            Once your request is verified and we have determined that we are
            required to delete that information in accordance with applicable
            law, we will delete your personal information accordingly. Your
            request to delete your personal information may be denied if it is
            necessary for us to retain your information under one or more of the
            exceptions listed in the State Privacy Laws. Please note that a
            record of your deletion request may be kept pursuant to our legal
            obligations.
          </Text>
        </li>
        <li className={styles['item-alpha-bold']}>
          <Heading variant="H6" className={styles.inline}>
            Correction:
          </Heading>
          <Text className={styles['inline-text']}>
            You may ask us to correct information that is inaccurate or
            incomplete, or you may update and correct certain of your personal
            information through your Hungryroot account.
          </Text>
        </li>
        <li className={styles['item-alpha-bold']}>
          <Heading variant="H6" className={styles.inline}>
            The right to opt out of “sales” and “sharing”(and the processing of
            personal information for targeted advertising more generally):
          </Heading>
          <Text className={styles['inline-text']}>
            The State Privacy Laws generally define the term “sale” very broadly
            to include transfers of personal information for valuable or
            monetary consideration, which includes even types of advertising.
            For example, our disclosure of identifiers to our analytics partners
            for marketing purposes qualifies as a “sale.” Per this definition,
            we have sold the following categories of personal information in the
            past twelve (12) months:
          </Text>
          <ul className={styles['list-indented-bullet']}>
            <li>
              <Text>Identifiers.</Text>
            </li>
            <li>
              <Text>Internet or other electronic network activity.</Text>
            </li>
            <li>
              <Text>Geolocation data.</Text>
            </li>
          </ul>
          <Paragraph>
            The State Privacy Laws generally define the term “share” to mean the
            transfer of a customer’s personal information by a business to a
            third party for cross-context behavioral advertising, including in
            situations where no money is exchanged between the parties. This
            also includes the processing of personal information for purposes of
            “targeted advertising,” as defined under applicable State Privacy
            Laws. Per this definition, we have shared the following categories
            of personal information in the past twelve (12) months:
          </Paragraph>
          <ul className={styles['list-indented-bullet']}>
            <li>
              <Text>Identifiers.</Text>
            </li>
            <li>
              <Text>Internet or other electronic network activity.</Text>
            </li>
            <li>
              <Text>Geolocation data.</Text>
            </li>
          </ul>
          <Paragraph className={styles['margin-bottom']}>
            We have sold or shared such categories of personal information with
            the following third parties through tracking technologies such as
            cookies and pixels: our advertising partners, advertising networks,
            marketing partners, and data analytics providers, including social
            networks.
          </Paragraph>
          <div>
            <Paragraph className={styles['margin-bottom']}>
              <span className={styles.bold}>
                You have the right to opt out of the “sale” and “sharing” of
                your personal information, as such terms are described above,
                including the processing of personal information for targeted
                advertising more generally.{' '}
              </span>
              If you wish to opt out of these uses of your personal information,
              you may either submit a request to{' '}
              <a
                href="mailto:help@hungryroot.com"
                className={styles.link}
                target="_blank"
              >
                help@hungryroot.com
              </a>
              , or complete our online form available{' '}
              <a
                href="https://airtable.com/shr3O0BHKqYS0uu5v"
                className={styles.link}
                target="_blank"
              >
                here
              </a>{' '}
              or when you click on the “Do Not Sell or Share My Personal
              Information” link available on our homepage.
            </Paragraph>
            <Paragraph>
              We do not have actual knowledge of “selling” or “sharing” personal
              information of those under sixteen (16) years of age.
            </Paragraph>
          </div>
        </li>
      </ol>
    ),
  },
  appeals: {
    title: 'Appeals and Non-Discrimination',
    body: (
      <Paragraph>
        You have a right to not receive discriminatory treatment for exercising
        your rights under the State Privacy Laws. Please note that we may not be
        able to provide you with certain of our Services if your having
        exercised certain of these rights limits our ability to process your
        personal information in certain contexts. In the event that we decline
        to fulfil a request to exercise one of the rights set forth above,
        Colorado and Virginia residents have the right to appeal our decision
        under their respective State Privacy Laws.
      </Paragraph>
    ),
  },
  access: {
    title: 'Exercising Your Access Requests',
    body: (
      <Fragment key="access">
        <Paragraph className={styles['margin-bottom']}>
          You can make an access, deletion, or correction request by contacting
          us at help@hungryroot.com and including “U.S. State Privacy Law
          Request” in the subject line.
        </Paragraph>
        <Paragraph className={styles['margin-bottom']}>
          You may also change or correct your personal information at any time,
          such as your email address, phone number or payment card information.
        </Paragraph>
        <Paragraph>
          Please note that only you, or a person that you authorize to act on
          your behalf, may make a request related to your personal information.
          We must take measures to verify your identity before processing your
          request. We will ask you to provide sufficient information for
          verification purposes, such as previous order history of the person to
          whom the request relates, or information that confirms that an
          authorized agent is able to act on your behalf. We will use this
          information only to verify your request. We will also need to verify
          the authority of any person acting on your behalf.
        </Paragraph>
      </Fragment>
    ),
  },
  incentive: {
    title: 'Notice of Financial Incentive',
    body: (
      <Fragment key="financial-incentive">
        <ol className={styles['list-inline-alpha']}>
          <li className={styles['item-alpha-bold']}>
            <Heading variant="H6" className={styles.inline}>
              Refer a friend:
            </Heading>
            <Text className={styles['inline-text']}>
              We provide a financial incentive to customers who refer their
              friends to our Services. If you provide a friend’s email address
              to Hungryroot via the referral link on our website, your friend
              will receive an email from us to sign up for a Hungryroot
              subscription. You’ll get $50 towards your next delivery when they
              sign up, and they’ll receive $50 off their first Hungryroot
              delivery. The categories of personal information that are
              implicated are your friend’s personal identifiers (specifically,
              their email address). You represent that you have the consent of
              any individual whose personal information you share with us in
              connection with opting in to this financial incentive, and that we
              may use such personal information in accordance with our Privacy
              Policy. Note that you may also opt in to the incentive program by
              navigating to your personal referral code in your Hungryroot
              account settings, under the tab “Give $50.” The referral code, if
              used by your friends, will give them each $50 off their first
              Hungryroot delivery, and you’ll get $50 towards your next delivery
              when they sign up. To opt in to this financial incentive, check
              the opt-in check box available in your Hungryroot account settings
              under the tab “Give $50”. You may withdraw your participation at
              any time by ceasing to provide your friends’ email addresses to us
              and foregoing the incentive. If you do not wish to participate, do
              not submit any referral information to us.
            </Text>
          </li>
          <li className={styles['item-alpha-bold']}>
            <Heading variant="H6" className={styles.inline}>
              30% off your first order and free gift for life upon account
              creation:
            </Heading>
            <Paragraph className={styles['inline-text']}>
              We also provide a financial incentive to individuals who create a
              new Hungryroot account and place a groceries plan order. If you
              sign up for a Hungryroot account through this promotion, you will
              receive 30% off the total amount of your fist order with a plan
              size of USD $99 or more, and a free gift included in every
              delivery you receive until you cancel your Hungryroot
              subscription. The categories of personal information that are
              implicated by this financial incentive are your personal
              identifiers and personal information under CA Code §1798.80(e)
              (specifically, your first and last name, alias (as applicable),
              shipping and mailing address, phone number, email address, and
              payment card number and related information). Please see
              additional material aspects of the financial incentive described{' '}
              <a
                href="https://support.hungryroot.com/hc/en-us"
                className={styles.link}
                target="_blank"
                rel="noreferrer"
              >
                here
              </a>
              . You may withdraw your participation at any time by contacting us
              at{' '}
              <a
                href="mailto:help@hungryroot.com"
                className={styles.link}
                target="_blank"
              >
                help@hungryroot.com
              </a>{' '}
              and foregoing the incentive. If you do not wish to participate,
              you do not need to create an account with Hungryroot or you may
              create an account with Hungryroot without opting in to the
              financial incentive.
            </Paragraph>
          </li>
        </ol>
        <Paragraph>
          Your participation in any of our financial incentives is voluntary and
          you must affirmatively opt in to each program as described above. The
          monetary value of each financial incentive is our good-faith,
          reasonable approximation of the monetary value of the information you
          can choose to provide to us. This amount is an estimate taking into
          account the anticipated revenue we expect to generate from new
          customers and the expenses related to our offer of each financial
          incentive.
        </Paragraph>
      </Fragment>
    ),
  },
  shineTheLight: {
    title: '“Shine the Light” Law',
    body: (
      <Paragraph>
        If you are a resident of California, you may request information
        regarding the disclosure of your personal information to third parties
        for the recipients’ own direct marketing purposes. To make such a
        request, please contact us in accordance with the “Contact Us” section
        below.
      </Paragraph>
    ),
  },
  policyChanges: {
    title: 'Changes to this State Law Privacy Notice',
    body: (
      <Paragraph>
        Any changes we may make to this State Law Privacy Notice will be posted
        on this page and, where appropriate, you may be notified by e-mail or in
        the App. Please check back frequently on our Site to see any updates to
        this State Law Privacy Notice. Your continued use of our Services
        following the posting of any updates will mean that you accept the
        changes we have made.
      </Paragraph>
    ),
  },
  contact: {
    title: 'Contact Us',
    body: (
      <Paragraph>
        If you have any questions or would like additional information about our
        privacy practices, please see our{' '}
        <a href="/privacy/" className={styles.link} target="_blank">
          Privacy Policy
        </a>{' '}
        or contact us at help@hungryroot.com.
      </Paragraph>
    ),
  },
};
